import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Phone, Mail, MessageCircle, Calendar, Edit, AlertTriangle } from 'lucide-react';
import api from '../../services/api';
import Badge from '../../components/shared/Badge';
import Modal from '../../components/shared/Modal';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

// Prazo de proteção do cliente do corretor parceiro (portal do corretor)
function textoProtecao(lead) {
  if (lead.atribuicao_status === 'conflito') return 'Em conflito — decida abaixo quem fica com o cliente.';
  if (!lead.protecao_ate) return 'Proteção sem prazo definido.';
  const fim = new Date(lead.protecao_ate);
  const dias = Math.ceil((fim - new Date()) / 86400000);
  const data = fim.toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' });
  return dias > 0 ? `Protegido até ${data} (${dias} ${dias === 1 ? 'dia' : 'dias'})` : `Proteção venceu em ${data}`;
}

const TIPOS_ATENDIMENTO = ['ligacao', 'whatsapp', 'email', 'visita', 'reuniao', 'proposta', 'outro'];

function fmtDataVisita(d) {
  if (!d) return 'data não informada';
  const [ano, mes, dia] = d.split('-');
  return dia && mes && ano ? `${dia}/${mes}/${ano}` : d;
}

// Agrupa as visitas (uma linha por visita) em candidatos únicos, com todas as datas de cada um.
function agruparCandidatos(candidatos) {
  const porCorretor = new Map();
  for (const c of candidatos || []) {
    if (!porCorretor.has(c.corretor_id)) porCorretor.set(c.corretor_id, { corretor_id: c.corretor_id, corretor_nome: c.corretor_nome, datas: [] });
    porCorretor.get(c.corretor_id).datas.push(c.data_visita);
  }
  return [...porCorretor.values()];
}

export default function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [modalAtend, setModalAtend] = useState(false);
  const [atendForm, setAtendForm] = useState({ tipo: 'ligacao', titulo: '', descricao: '', resultado: '' });

  const { data, isLoading } = useQuery({
    queryKey: ['lead', id],
    queryFn: () => api.get(`/leads/${id}`).then(r => r.data.data),
  });

  const addAtend = useMutation({
    mutationFn: (body) => api.post('/atendimentos', { lead_id: id, ...body }),
    onSuccess: () => { qc.invalidateQueries(['lead', id]); setModalAtend(false); },
  });

  const updateEstagio = useMutation({
    mutationFn: (estagio_funil) => api.put(`/leads/${id}`, { estagio_funil }),
    onSuccess: () => qc.invalidateQueries(['lead', id]),
  });

  // Descartar como inválido (telefone errado, cadastro falso…) — se veio do portal do corretor, o
  // ponto do cadastro é estornado automaticamente pelo portal.
  const descartarMut = useMutation({
    mutationFn: () => api.put(`/leads/${id}`, { estagio_funil: 'perdido', perdido_motivo: 'invalido' }),
    onSuccess: () => qc.invalidateQueries(['lead', id]),
  });

  const resolverMut = useMutation({
    mutationFn: (corretor_id) => api.post(`/leads/${id}/resolver-atribuicao`, { corretor_id }),
    onSuccess: () => qc.invalidateQueries(['lead', id]),
  });

  if (isLoading) return <div className="p-8 text-center text-gray-400">Carregando...</div>;
  if (!data) return <div className="p-8 text-center text-gray-400">Lead não encontrado</div>;

  const lead = data;
  const candidatosAgrupados = agruparCandidatos(lead.candidatos_atribuicao);

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/leads')} className="text-gray-500 hover:text-gray-700"><ArrowLeft size={20} /></button>
        <h1 className="text-2xl font-bold text-gray-800">{lead.nome}</h1>
        <Badge label={lead.estagio_funil?.replace(/_/g, ' ')} variant={lead.estagio_funil} />
      </div>

      {lead.atribuicao_status === 'conflito' && (
        <div className="bg-red-50 border-2 border-red-200 rounded-xl p-5 space-y-3">
          <div className="flex items-center gap-2 text-red-700 font-semibold">
            <AlertTriangle size={18} /> Conflito de atribuição — cliente reivindicado por corretor parceiro
          </div>
          <p className="text-sm text-red-600">
            A atribuição não muda sozinha em caso de conflito — escolha manualmente quem fica com este lead.
          </p>
          <div className="space-y-2">
            {candidatosAgrupados.map(c => (
              <div key={c.corretor_id} className="flex items-center justify-between bg-white rounded-lg px-4 py-2 border border-red-100">
                <div>
                  <p className="font-medium text-gray-800">{c.corretor_nome}</p>
                  <p className="text-xs text-gray-500">Registros: {c.datas.filter(Boolean).map(fmtDataVisita).join(', ') || 'dono atual'}</p>
                </div>
                <button
                  onClick={() => resolverMut.mutate(c.corretor_id)}
                  disabled={resolverMut.isPending}
                  className="px-3 py-1.5 bg-primary text-white rounded-lg text-xs font-medium hover:bg-secondary disabled:opacity-50 whitespace-nowrap"
                >
                  Atribuir a {c.corretor_nome?.split(' ')[0]}
                </button>
              </div>
            ))}
          </div>
          <button
            onClick={() => resolverMut.mutate(null)}
            disabled={resolverMut.isPending}
            className="text-xs text-gray-500 hover:text-gray-700 underline disabled:opacity-50"
          >
            Não atribuir a ninguém (fica com a inBase)
          </button>
          {resolverMut.isError && (
            <p className="text-sm text-red-600">{resolverMut.error?.response?.data?.error || 'Erro ao resolver atribuição'}</p>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Dados do Lead */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white rounded-xl shadow p-6">
            <h2 className="font-semibold text-gray-700 mb-4">Informações</h2>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-400">Telefone</p>
                <a href={`tel:${lead.telefone}`} className="text-primary font-medium flex items-center gap-1 mt-1">
                  <Phone size={14} />{lead.telefone}
                </a>
              </div>
              {lead.email && <div>
                <p className="text-gray-400">E-mail</p>
                <a href={`mailto:${lead.email}`} className="text-primary font-medium flex items-center gap-1 mt-1">
                  <Mail size={14} />{lead.email}
                </a>
              </div>}
              <div>
                <p className="text-gray-400">Origem</p>
                <Badge label={lead.origem?.replace(/_/g, ' ')} variant={lead.origem} />
              </div>
              <div>
                <p className="text-gray-400">Temperatura</p>
                <p className="font-medium mt-1">{lead.temperatura === 'quente' ? '🔴 Quente' : lead.temperatura === 'morno' ? '🟡 Morno' : '🔵 Frio'}</p>
              </div>
              {lead.empreendimento_nome && <div>
                <p className="text-gray-400">Interesse</p>
                <p className="font-medium mt-1">{lead.empreendimento_nome}</p>
              </div>}
              {lead.trazido_por_nome && <div className="col-span-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                <p className="text-amber-800 text-xs font-semibold uppercase tracking-wide">Corretor parceiro</p>
                <p className="font-medium mt-1 text-gray-800">
                  Trazido por: {lead.trazido_por_nome}{lead.trazido_por_imobiliaria ? ` / ${lead.trazido_por_imobiliaria}` : ''}
                </p>
                <p className="text-xs text-gray-600 mt-0.5">{textoProtecao(lead)}</p>
              </div>}
              <div>
                <p className="text-gray-400">Responsável</p>
                <p className="font-medium mt-1">{lead.responsavel_nome || '—'}</p>
              </div>
              <div>
                <p className="text-gray-400">Criado em</p>
                <p className="font-medium mt-1">{format(new Date(lead.created_at), 'dd/MM/yyyy HH:mm', { locale: ptBR })}</p>
              </div>
              {lead.utm_campaign && <div>
                <p className="text-gray-400">Campanha</p>
                <p className="font-medium mt-1 text-xs">{lead.utm_campaign}</p>
              </div>}
            </div>
            {lead.observacoes && (
              <div className="mt-4 pt-4 border-t">
                <p className="text-gray-400 text-sm">Observações</p>
                <p className="text-gray-700 mt-1">{lead.observacoes}</p>
              </div>
            )}
          </div>

          {/* Timeline de Atendimentos */}
          <div className="bg-white rounded-xl shadow p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-semibold text-gray-700">Timeline</h2>
              <button onClick={() => setModalAtend(true)} className="flex items-center gap-2 px-3 py-1.5 bg-primary text-white rounded-lg text-xs hover:bg-secondary">
                <MessageCircle size={14} /> Registrar Atendimento
              </button>
            </div>
            <div className="space-y-4">
              {(lead.atendimentos || []).map(a => (
                <div key={a.id} className="flex gap-3">
                  <div className="w-2 h-2 bg-secondary rounded-full mt-2 flex-shrink-0" />
                  <div className="flex-1 pb-4 border-b border-gray-50 last:border-0">
                    <div className="flex justify-between">
                      <p className="font-medium text-sm text-gray-700">{a.titulo || a.tipo?.replace(/_/g, ' ')}</p>
                      <p className="text-xs text-gray-400">{format(new Date(a.created_at), 'dd/MM HH:mm', { locale: ptBR })}</p>
                    </div>
                    {a.descricao && <p className="text-sm text-gray-500 mt-1 whitespace-pre-line">{a.descricao}</p>}
                    <p className="text-xs text-gray-400 mt-1">por {a.responsavel_nome}</p>
                  </div>
                </div>
              ))}
              {!lead.atendimentos?.length && <p className="text-gray-400 text-sm">Nenhum atendimento registrado</p>}
            </div>
          </div>

          {/* Conversa no WhatsApp com o Iago Vendedor (ao vivo) */}
          {lead.conversa_whatsapp?.length > 0 && (
            <div className="bg-white rounded-xl shadow p-6">
              <h2 className="font-semibold text-gray-700 mb-4">Conversa no WhatsApp ({lead.conversa_whatsapp.length})</h2>
              <div className="space-y-2 max-h-[32rem] overflow-y-auto bg-gray-50 rounded-lg p-3">
                {lead.conversa_whatsapp.map(m => (
                  <div key={m.id} className={`flex ${m.direction === 'in' ? 'justify-start' : 'justify-end'}`}>
                    <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${m.direction === 'in' ? 'bg-white text-gray-700' : 'bg-green-100 text-gray-800'}`}>
                      <p className="text-[11px] font-medium text-gray-400 mb-0.5">
                        {m.direction === 'in' ? lead.nome : m.message_type === 'human' ? 'Fernando' : 'Iago'} · {format(new Date(m.created_at), 'dd/MM HH:mm', { locale: ptBR })}
                      </p>
                      <p className="whitespace-pre-line break-words">{m.content}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar Ações */}
        <div className="space-y-4">
          <div className="bg-white rounded-xl shadow p-6">
            <h2 className="font-semibold text-gray-700 mb-4">Mudar Estágio</h2>
            <div className="space-y-2">
              {['novo','contato_feito','visita_agendada','proposta_enviada','negociacao','reservado','vendido','perdido'].map(e => (
                <button
                  key={e}
                  onClick={() => updateEstagio.mutate(e)}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${lead.estagio_funil === e ? 'bg-primary text-white' : 'hover:bg-gray-50 text-gray-600'}`}
                >
                  {e.replace(/_/g, ' ')}
                </button>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-xl shadow p-6">
            <h2 className="font-semibold text-gray-700 mb-3">Ações Rápidas</h2>
            <div className="space-y-2">
              <a href={`https://wa.me/55${lead.telefone?.replace(/\D/g, '')}`} target="_blank" rel="noreferrer"
                className="flex items-center gap-2 w-full px-3 py-2 bg-green-50 text-green-700 rounded-lg text-sm hover:bg-green-100">
                <MessageCircle size={16} /> Abrir WhatsApp
              </a>
              <a href={`tel:${lead.telefone}`}
                className="flex items-center gap-2 w-full px-3 py-2 bg-blue-50 text-blue-700 rounded-lg text-sm hover:bg-blue-100">
                <Phone size={16} /> Ligar
              </a>
              {!(lead.estagio_funil === 'perdido' && lead.perdido_motivo === 'invalido') ? (
                <button
                  onClick={() => { if (window.confirm('Descartar este lead como inválido? Se veio do portal do corretor, o ponto do cadastro é estornado.')) descartarMut.mutate(); }}
                  disabled={descartarMut.isPending}
                  className="flex items-center gap-2 w-full px-3 py-2 bg-gray-50 text-gray-600 rounded-lg text-sm hover:bg-gray-100 disabled:opacity-50">
                  <AlertTriangle size={16} /> Descartar como inválido
                </button>
              ) : <p className="text-xs text-gray-500 px-1">Descartado como inválido.</p>}
            </div>
          </div>
        </div>
      </div>

      {/* Modal Atendimento */}
      <Modal isOpen={modalAtend} onClose={() => setModalAtend(false)} title="Registrar Atendimento">
        <form onSubmit={e => { e.preventDefault(); addAtend.mutate(atendForm); }} className="space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-700">Tipo</label>
            <select value={atendForm.tipo} onChange={e => setAtendForm(p => ({ ...p, tipo: e.target.value }))}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 mt-1 text-sm">
              {TIPOS_ATENDIMENTO.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Título</label>
            <input value={atendForm.titulo} onChange={e => setAtendForm(p => ({ ...p, titulo: e.target.value }))}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 mt-1 text-sm" />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Descrição</label>
            <textarea value={atendForm.descricao} onChange={e => setAtendForm(p => ({ ...p, descricao: e.target.value }))}
              rows={4} className="w-full border border-gray-200 rounded-lg px-3 py-2 mt-1 text-sm" />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Resultado</label>
            <select value={atendForm.resultado} onChange={e => setAtendForm(p => ({ ...p, resultado: e.target.value }))}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 mt-1 text-sm">
              <option value="">Selecionar...</option>
              <option value="positivo">Positivo</option>
              <option value="neutro">Neutro</option>
              <option value="negativo">Negativo</option>
              <option value="sem_retorno">Sem retorno</option>
            </select>
          </div>
          <button type="submit" disabled={addAtend.isPending}
            className="w-full bg-primary text-white py-2 rounded-lg text-sm font-medium hover:bg-secondary disabled:opacity-50">
            {addAtend.isPending ? 'Salvando...' : 'Salvar Atendimento'}
          </button>
        </form>
      </Modal>
    </div>
  );
}
